import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ArrowRight, Loader2 } from 'lucide-react';
import { PRIZES, useLeaderboard } from '../services/leaderboardService';

const LeaderboardPreview = () => {
  // Use the leaderboard hook to fetch the data
  const { totalPrizePool, loading, cycleStartDate, cycleEndDate } = useLeaderboard();

  // Format cycle duration for display
  const cycleDuration = cycleStartDate && cycleEndDate ?
    Math.round((cycleEndDate.getTime() - cycleStartDate.getTime()) / (1000 * 60 * 60 * 24)) :
    7;

  const prizeFrequency = cycleDuration === 7 ? 'weekly' :
    cycleDuration === 14 ? 'bi-weekly' :
      `${cycleDuration}-day`;

  return (
    <section id="leaderboard-preview" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-[#D7FF00] to-white bg-clip-text text-transparent">
          Exclusive Wager Leaderboards
        </h2>
        <div className="bg-[#D7FF00]/5 border border-[#D7FF00]/20 rounded-2xl p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-6">
                <Trophy className="w-12 h-12 text-[#D7FF00]" />
                <h2 className="text-3xl font-bold">Goated Leaderboard</h2>
              </div>
              <p className="text-gray-400 text-lg mb-8">
                Compete with other players for a share of ${totalPrizePool} in {prizeFrequency} prizes. Track your progress and climb the ranks to earn rewards.
              </p>
              <Link
                to="/leaderboard"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D7FF00] text-black rounded-xl font-bold hover:opacity-90 transition-opacity"
              >
                View Full Leaderboard
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="flex-1 w-full max-w-md">
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                {loading ? (
                  <div className="py-12 flex justify-center items-center">
                    <Loader2 className="animate-spin h-8 w-8 text-[#D7FF00]" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <span>Top Prizes</span>
                      <span>Amount</span>
                    </div>
                    {/* First Place */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#D7FF00]/10 flex items-center justify-center">
                          <span className="font-bold text-[#D7FF00]">1</span>
                        </div>
                        <span className="font-semibold">First Place</span>
                      </div>
                      <span className="font-bold text-[#D7FF00]">${PRIZES[1]}</span>
                    </div>
                    {/* Second Place */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#C0C0C0]/10 flex items-center justify-center">
                          <span className="font-bold text-[#C0C0C0]">2</span>
                        </div>
                        <span className="font-semibold">Second Place</span>
                      </div>
                      <span className="font-bold text-[#C0C0C0]">${PRIZES[2]}</span>
                    </div>
                    {/* Third Place */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#CD7F32]/10 flex items-center justify-center">
                          <span className="font-bold text-[#CD7F32]">3</span>
                        </div>
                        <span className="font-semibold">Third Place</span>
                      </div>
                      <span className="font-bold text-[#CD7F32]">${PRIZES[3]}</span>
                    </div>
                    {/* Places 4-10 */}
                    <div className="pt-2 border-t border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                            <span className="font-bold text-white/80">4&ndash;10</span>
                          </div>
                          <span className="font-semibold">Other Places</span>
                        </div>
                        <span className="font-bold text-white/80">${PRIZES[4]}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeaderboardPreview;